import { ChevronRight, PanelLeftClose, Search, Settings, Sparkles, SquarePen } from "lucide-react";
import { useState } from "react";
import type { Conversation } from "../types";
import { IconButton } from "./IconButton";

type SidebarProps = {
  conversations: Conversation[];
  activeId: string;
  isOpen: boolean;
  onClose: () => void;
  onNewChat: () => void;
  onSelect: (id: string) => void;
  onOpenSettings: () => void;
  displayName?: string;
  connectionLabel: string;
};

const sourceLabels: Record<string, string> = {
  webui: "Web",
  slack: "Slack",
  cli: "CLI",
  discord: "Discord",
  telegram: "Telegram",
  whatsapp: "WhatsApp",
};

function sourceLabel(source: string) {
  if (sourceLabels[source]) return sourceLabels[source];
  if (source === "other") return "기타";
  return source.replace(/[-_]+/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function Sidebar({ conversations, activeId, isOpen, onClose, onNewChat, onSelect, onOpenSettings, displayName = "Jaeyoung", connectionLabel }: SidebarProps) {
  const [query, setQuery] = useState("");
  const normalizedQuery = query.trim().toLocaleLowerCase("ko-KR");
  const sources = Array.from(new Set(conversations.map((conversation) => conversation.source)));

  return (
    <aside className={`sidebar ${isOpen ? "is-open" : ""}`} aria-label="대화 목록">
      <div className="sidebar__header">
        <button className="sidebar__brand" type="button" onClick={onNewChat} aria-label="Hermes 새 대화">
          <span className="sidebar__brand-mark"><Sparkles size={16} /></span>
          <strong>Hermes</strong>
        </button>
        <IconButton label="사이드바 닫기" onClick={onClose}><PanelLeftClose size={20} /></IconButton>
      </div>

      <nav className="sidebar__primary" aria-label="주요 메뉴">
        <button type="button" onClick={onNewChat}><SquarePen size={19} /><span>새 채팅</span><kbd>⌘ N</kbd></button>
        <label htmlFor="conversation-search">
          <Search size={19} />
          <input
            id="conversation-search"
            placeholder="대화 검색"
            autoComplete="off"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <kbd>⌘ K</kbd>
        </label>
      </nav>

      <nav className="conversation-list" aria-label="최근 대화">
        {sources.map((source) => {
          const items = conversations.filter((conversation) =>
            conversation.source === source
            && (!normalizedQuery || `${conversation.title} ${conversation.preview}`.toLocaleLowerCase("ko-KR").includes(normalizedQuery)),
          ).slice(0, 5);
          if (!items.length) return null;
          return (
            <section className="conversation-group" key={source}>
              <h2>{sourceLabel(source)}</h2>
              {items.map((conversation) => (
                <button
                  type="button"
                  className={`conversation-item ${activeId === conversation.id ? "is-active" : ""}`}
                  key={conversation.id}
                  onClick={() => onSelect(conversation.id)}
                  aria-current={activeId === conversation.id ? "page" : undefined}
                >
                  <span>{conversation.title}</span>
                  <span className="conversation-item__more" aria-hidden="true">•••</span>
                </button>
              ))}
            </section>
          );
        })}
      </nav>

      <div className="sidebar__footer">
        <button type="button" onClick={onOpenSettings}>
          <span className="account-avatar">{displayName.trim().slice(0, 2).toUpperCase()}</span>
          <span className="account-copy"><strong>{displayName}</strong><small>화면 설정</small></span>
          <ChevronRight size={16} />
        </button>
        <span className="sidebar__ready"><Settings size={13} /> {connectionLabel}</span>
      </div>
    </aside>
  );
}
